import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  TextField,
  Stack,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";

import UploadCertModal from "./component/M_UploadCert";
import ConfirmGenerateCertModal from "./component/M_ConfirmGenerateCert";
import GenerateCertModal from "./component/M_GenerateCert";
import UploadOriginCertModal from "./component/M_UploadOriginCert";

import { CertificateType, UserType, WAF_EDGE_DOMAIN } from "../../../utils/constants";
import { formatDate } from "../../../utils/format";

import useAuth from "../../../hooks/useAuth";
import useWAFEdge from "../../../hooks/super/nodes/useWAFEdge";

import {
  CollapseAlert,
  IOSSwitch,
  LoadingButton,
  SnackbarAlert,
} from "../../../components/pages/common/styled";
import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import OutlinedButton from "../../../components/pages/_new/super/common/OutlinedButton";
import {
  TOKENS,
  SectionCard,
  StatusPill,
} from "../../../components/pages/_new/super/design";

const FieldLabel = ({ children }) => (
  <Typography
    sx={{ fontSize: 12.5, fontWeight: 600, color: TOKENS.fg2, mb: 0.5 }}
  >
    {children}
  </Typography>
);

const expiryChip = (validTo) => {
  if (!validTo) return "—";
  const d = new Date(validTo);
  const now = new Date();
  const daysLeft = Math.floor((d - now) / 86400000);
  if (d <= now) {
    return (
      <Typography
        component="span"
        sx={{
          fontSize: 12.5,
          color: TOKENS.dangerInk,
          background: TOKENS.dangerBg,
          border: `1px solid ${TOKENS.dangerBorder}`,
          borderRadius: 999,
          px: "10px",
          py: "2px",
          fontWeight: 600,
        }}
      >
        Expired · {formatDate(validTo)}
      </Typography>
    );
  }
  if (daysLeft < 30) {
    return (
      <Typography
        component="span"
        sx={{
          fontSize: 12.5,
          color: TOKENS.warningInk,
          background: TOKENS.warningBg,
          border: `1px solid ${TOKENS.warningBorder}`,
          borderRadius: 999,
          px: "10px",
          py: "2px",
          fontWeight: 600,
        }}
      >
        {daysLeft}d · {formatDate(validTo)}
      </Typography>
    );
  }
  return (
    <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>
      {formatDate(validTo)}
    </Typography>
  );
};

function SAWAFConfig() {
  const {
    errMsg,
    setErr,
    getCertificate,
    certs,
    https_enabled,
    httpsEnableChange,
    sslApply,
    clearWildcardCerts,
  } = useWAFEdge();
  const { isAuthenticated, adminUserType } = useAuth();
  const isSuper = UserType.SUPER_ADMIN === adminUserType;

  const [errOpen, setErrOpen] = React.useState(false);
  const [rootDomain, setRootDomain] = React.useState(WAF_EDGE_DOMAIN);
  const changeRootDomain = (e) => setRootDomain(e.target.value);
  let reloadTimer = null;

  const [snackOpen, setSnackOpen] = React.useState(false);
  const handleSnackClose = () => setSnackOpen(false);
  const [message, setMessage] = React.useState();
  const [success, setSuccess] = React.useState();
  const [loading, setLoading] = React.useState(false);

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [openOrigin, setOpenOrigin] = React.useState(false);
  const handleOpenOrigin = () => setOpenOrigin(true);
  const handleCloseOrigin = () => setOpenOrigin(false);

  const [confirmGenerateCertOpen, setConfirmGenerateCertOpen] = React.useState(false);
  const handleConfirmGenerateCertClose = () => setConfirmGenerateCertOpen(false);

  const [generateCertOpen, setGenerateCertOpen] = React.useState(false);
  const handleGenerateCertClose = () => {
    setGenerateCertOpen(false);
    clearWildcardCerts();
  };
  const handleGenerateCertComplete = () => {
    getCertificate();
    reloadTimer = setTimeout(() => {
      getCertificate();
    }, 30 * 1000);
  };
  const handleGenerateWildcard = () => {
    setConfirmGenerateCertOpen(false);
    setGenerateCertOpen(true);
  };

  const [httpsEnable, setHttpsEnable] = React.useState(https_enabled);
  const changeHttpsEnable = () => {
    httpsEnableChange(!httpsEnable);
    setHttpsEnable(!httpsEnable);
  };

  const apply = async () => {
    if (certs === null) {
      setErr("You must upload or generate your own certificate");
      return;
    }
    setLoading(true);
    const result = await sslApply();
    setMessage(result.msg);
    setSuccess(result.status);
    setLoading(false);
    setSnackOpen(true);
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) getCertificate();
    return () => setErr(null);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    return () => {
      if (reloadTimer) clearTimeout(reloadTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    setHttpsEnable(https_enabled);
  }, [https_enabled]);
  React.useEffect(() => {
    if (errMsg) setErrOpen(true);
  }, [errMsg]);

  return (
    <React.Fragment>
      <Helmet title="SA WAF Configuration" />
      <PageHeader
        title="WAF Engines Configuration"
        description="Manage TLS certificates and HTTPS termination for WAF edges"
        actions={
          <LoadingButton
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            loadingPosition="start"
            loading={loading}
            onClick={apply}
            disabled={!isSuper}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: `${TOKENS.radiusXs}px`,
              px: 2,
            }}
          >
            Apply
          </LoadingButton>
        }
      />
      <Box sx={{ mb: 3 }}>
        <CollapseAlert errOpen={errOpen} setErrOpen={setErrOpen} setErr={setErr} errMsg={errMsg} />
      </Box>

      {certs === null ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: "grid", gap: 2 }}>
          <SectionCard
            title="Current Certificate"
            description="Active TLS certificate served by the WAF edges"
          >
            <Box
              sx={{
                border: `1px solid ${TOKENS.borderSubtle}`,
                borderRadius: `${TOKENS.radiusS}px`,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0,2fr) 1fr 1fr 1fr",
                  px: 2,
                  py: 1.25,
                  background: TOKENS.bgSunken,
                  borderBottom: `1px solid ${TOKENS.borderSubtle}`,
                }}
              >
                {["Host", "Status", "Type", "Expires"].map((h) => (
                  <Typography
                    key={h}
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: TOKENS.fg2,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {h}
                  </Typography>
                ))}
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0,2fr) 1fr 1fr 1fr",
                  px: 2,
                  py: 1.5,
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    color: TOKENS.fg1,
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {certs?.host || "—"}
                </Typography>
                <Box>
                  {certs?.type === CertificateType.CUSTOM ? (
                    <StatusPill status={certs?.host ? "Active" : "Expired"} />
                  ) : certs?.type === CertificateType.SENSE_GUARD ? (
                    <StatusPill status={certs?.host ? "Active" : "Suspended"} />
                  ) : (
                    "—"
                  )}
                </Box>
                <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>
                  {certs
                    ? certs?.type === CertificateType.SENSE_GUARD
                      ? "Universal"
                      : "User Managed"
                    : "—"}
                </Typography>
                <Box>{expiryChip(certs?.validTo)}</Box>
              </Box>
            </Box>
          </SectionCard>

          <SectionCard title="SSL Settings">
            <Stack spacing={3}>
              <Box>
                <FieldLabel>Root Domain</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  value={rootDomain}
                  onChange={changeRootDomain}
                />
              </Box>

              <Box>
                <FieldLabel>Install SSL Certificates</FieldLabel>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={4}>
                    <OutlinedButton onClick={handleOpen} disabled={!isSuper} sx={{ width: "100%" }}>
                      Upload SSL Certificate
                    </OutlinedButton>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <OutlinedButton
                      onClick={handleOpenOrigin}
                      disabled={!isSuper}
                      sx={{ width: "100%" }}
                    >
                      Generate Wildcard Certificate
                    </OutlinedButton>
                  </Grid>
                </Grid>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  borderTop: `1px solid ${TOKENS.borderSubtle}`,
                  pt: 2,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: TOKENS.fg1 }}>
                    Enable HTTPS
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: TOKENS.fg3, mt: 0.25 }}>
                    Force TLS termination on the WAF edge layer.
                  </Typography>
                </Box>
                <IOSSwitch
                  checked={httpsEnable}
                  onChange={changeHttpsEnable}
                  disabled={!isSuper}
                />
              </Box>
            </Stack>
          </SectionCard>
        </Box>
      )}

      <UploadCertModal open={open} handleClose={handleClose} />
      <ConfirmGenerateCertModal
        open={confirmGenerateCertOpen}
        handleClose={handleConfirmGenerateCertClose}
        handleConfirm={handleGenerateWildcard}
      />
      <GenerateCertModal
        open={generateCertOpen}
        siteID={rootDomain}
        handleClose={handleGenerateCertClose}
        handleComplete={handleGenerateCertComplete}
      />
      <UploadOriginCertModal rootDomain={rootDomain} open={openOrigin} handleClose={handleCloseOrigin} />
      <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
    </React.Fragment>
  );
}

export default SAWAFConfig;
